/**
 * Admin → Quality Issues page.
 *
 * Surfaces every response that was either voided post-hoc (straightlining,
 * fast-fill strikes, duplicate IP) or screened out at intake (quota fill,
 * hard-screener rule). One row per affected response, joined with user +
 * wave metadata so the admin can act without context-switching.
 *
 * Side-effect: on mount we POST /admin/quality-issues/mark-seen so the
 * sidebar badge clears. Returning to the page later only shows the badge
 * again if NEW rows have landed since the last visit.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { formatDateTime } from "../lib/date";

const KIND_LABEL = {
  voided: { label: "VOIDED", className: "bg-red-50 text-red-700 border-red-300" },
  screened: { label: "SCREENED", className: "bg-amber-50 text-amber-800 border-amber-300" },
};

// Map cryptic reason codes to plain-English admin tooltips. Anything that
// doesn't match falls through verbatim — better to show "fast_fill_strike_2:14.9s"
// raw than to hide signal.
const REASON_HINT = {
  fast_fill: "User completed too quickly (likely speeder)",
  straightline: "User picked the same answer for many questions in a row",
  duplicate_ip: "Another response from the same IP this wave",
  quota_full: "Quota for this user's region/age/gender was already full",
  screener_failed: "User answered a hard-screener question outside the target",
};

function prettyReason(raw) {
  if (!raw || raw === "—") return raw;
  const base = String(raw).split(":")[0];
  return REASON_HINT[base] || raw;
}

export default function AdminQualityIssues() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState(""); // "" | "voided" | "screened"
  const [search, setSearch] = useState("");
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  // Load list + mark-seen in parallel so the badge clears the moment the
  // admin lands on this page. mark-seen failure is non-fatal — the worst
  // case is a stale badge.
  useEffect(() => {
    setLoading(true);
    api.get("/admin/quality-issues", { params: kind ? { kind } : {} })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    api.post("/admin/quality-issues/mark-seen").catch(() => {});
  }, [kind]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) =>
      (r.user_name || "").toLowerCase().includes(q) ||
      (r.user_email || "").toLowerCase().includes(q) ||
      (r.wave_title || "").toLowerCase().includes(q) ||
      (r.reason || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  // Reset to page 1 whenever the filtered set could change shape.
  useEffect(() => { setPage(1); }, [kind, search]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paginated = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const counts = useMemo(() => ({
    voided: items.filter((r) => r.kind === "voided").length,
    screened: items.filter((r) => r.kind === "screened").length,
    total: items.length,
  }), [items]);

  return (
    <div data-testid="admin-quality-issues">
      <div className="mb-6">
        <div className="overline text-neutral-500">DATA QUALITY</div>
        <h1 className="text-3xl font-black tracking-tighter">Quality issues</h1>
        <p className="text-sm text-neutral-600 mt-1 max-w-2xl">
          Every response that was rejected at intake or voided post-hoc.
          Use this to spot bad actors, tighten quota allocations, and audit
          the platform&apos;s quality-control rules.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <Stat label="Total issues" value={counts.total} testid="stat-total" />
        <Stat label="Voided" value={counts.voided} className="text-red-700" testid="stat-voided" />
        <Stat label="Screened out" value={counts.screened} className="text-amber-700" testid="stat-screened" />
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-1 border border-neutral-300">
          {[
            { v: "", l: "All" },
            { v: "voided", l: "Voided only" },
            { v: "screened", l: "Screened only" },
          ].map((r) => (
            <button
              key={r.v}
              onClick={() => setKind(r.v)}
              data-testid={`qi-filter-${r.v || "all"}`}
              className={`px-3 py-1.5 text-xs ${kind === r.v ? "bg-black text-white" : "hover:bg-neutral-100"}`}
            >{r.l}</button>
          ))}
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search user, wave, or reason…"
          className="px-3 py-1.5 text-sm border border-neutral-300 w-72"
          data-testid="qi-search"
        />
        <div className="ml-auto text-xs text-neutral-500 font-mono">
          Showing {filtered.length} of {items.length}
        </div>
      </div>

      {loading ? (
        <div className="text-neutral-400 py-12 text-center" data-testid="qi-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-neutral-300 p-12 text-center" data-testid="qi-empty">
          <div className="font-bold text-neutral-700">No quality issues to review</div>
          <div className="text-xs text-neutral-500 mt-1">
            New voided or screened-out responses will appear here automatically.
          </div>
        </div>
      ) : (
        <div className="border-2 border-black overflow-x-auto hard-shadow">
          <table className="w-full text-sm" data-testid="qi-table">
            <thead className="bg-neutral-50 border-b-2 border-black">
              <tr>
                <Th>When</Th>
                <Th>Kind</Th>
                <Th>User</Th>
                <Th>Wave</Th>
                <Th>Reason</Th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((r) => {
                const k = KIND_LABEL[r.kind] || KIND_LABEL.voided;
                return (
                  <tr key={r.id} className="border-b border-neutral-200" data-testid={`qi-row-${r.id}`}>
                    <td className="py-3 px-3 whitespace-nowrap text-xs font-mono text-neutral-600">
                      {fmtDate(r.completed_at || r.started_at)}
                    </td>
                    <td className="py-3 px-3">
                      <span className={`text-[10px] px-2 py-0.5 border font-bold tracking-wider ${k.className}`}>
                        {k.label}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      <div className="font-medium">{r.user_name}</div>
                      <div className="text-xs text-neutral-500 font-mono">{r.user_email}</div>
                      {r.user_suspended && (
                        <span className="text-[10px] text-red-700 font-bold uppercase">Suspended</span>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <div className="font-medium">{r.wave_title || "—"}</div>
                      {r.wave_status && (
                        <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{r.wave_status}</div>
                      )}
                      {r.wave_id && (
                        <Link to={`/admin/waves/${r.wave_id}/report`} className="text-[10px] text-neutral-500 underline hover:text-black">View report →</Link>
                      )}
                    </td>
                    <td className="py-3 px-3 text-xs">
                      <div className="font-mono text-neutral-700">{r.reason}</div>
                      <div className="text-neutral-500 mt-0.5">{prettyReason(r.reason)}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="flex items-center justify-between gap-3 mt-4 flex-wrap" data-testid="qi-pagination">
          <div className="text-xs text-neutral-500 font-mono">
            Page {pageSafe} of {totalPages} · rows {(pageSafe - 1) * PAGE_SIZE + 1}–{Math.min(pageSafe * PAGE_SIZE, filtered.length)} of {filtered.length}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pageSafe <= 1}
              className="px-3 py-1.5 text-xs font-bold border-2 border-black disabled:opacity-30 disabled:cursor-not-allowed hover:bg-black hover:text-white transition-colors"
              data-testid="qi-page-prev"
            >← Prev</button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={pageSafe >= totalPages}
              className="px-3 py-1.5 text-xs font-bold border-2 border-black disabled:opacity-30 disabled:cursor-not-allowed hover:bg-black hover:text-white transition-colors"
              data-testid="qi-page-next"
            >Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, className = "", testid }) {
  return (
    <div className="border-2 border-black p-4 hard-shadow" data-testid={testid}>
      <div className="overline text-neutral-500">{label}</div>
      <div className={`text-3xl font-black tracking-tighter mt-1 ${className}`}>{value}</div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left py-3 px-3 overline text-neutral-600">{children}</th>;
}

function fmtDate(iso) {
  return formatDateTime(iso);
}
